/**
 * The live connection behind Rooms.
 *
 * One WebSocket per signed-in member, to their own channel. Everything they
 * are allowed to hear — a message in any of their rooms, a read count, a
 * membership change — arrives on it, so joining a room costs no extra
 * connection.
 *
 * The token names the member's handle, never their account, and lasts fifteen
 * minutes; the client fetches a fresh one before it expires. Reconnecting,
 * backing off and replaying what was missed are the library's job. Anything
 * older than Centrifugo's short history is caught up over the API instead,
 * which is why every message carries its number in the room.
 */

import { Centrifuge, type Subscription } from "centrifuge";

import { getConnectionToken } from "@/lib/services/rooms";

export type LiveEvent =
  | { type: "room.message"; room: string; message: unknown }
  | { type: "room.message_changed"; room: string; message: unknown }
  | { type: "room.read"; room: string; seq: number; seen_by: number }
  | { type: "room.membership"; room: string; member_count: number };

type Listener = (event: LiveEvent) => void;

const listeners = new Set<Listener>();
let client: Centrifuge | null = null;
let subscription: Subscription | null = null;
let starting: Promise<void> | null = null;
let connected = false;
const connectionListeners = new Set<(up: boolean) => void>();

function websocketUrl(): string {
  const host = import.meta.env.VITE_WS_HOST || window.location.host;
  const scheme = window.location.protocol === "https:" ? "wss" : "ws";
  return `${scheme}://${host}/connection/websocket`;
}

function announce(event: LiveEvent) {
  listeners.forEach((listener) => listener(event));
}

function setConnected(up: boolean) {
  connected = up;
  connectionListeners.forEach((listener) => listener(up));
}

/** Open the connection, or do nothing if it is already open. */
async function start(): Promise<void> {
  if (client) return;
  if (starting) return starting;

  starting = (async () => {
    const first = await getConnectionToken();
    const centrifuge = new Centrifuge(websocketUrl(), {
      token: first.token,
      // Called when the current token is about to expire. A failure here closes
      // the connection rather than leaving a member connected on a dead token.
      getToken: async () => (await getConnectionToken()).token,
    });

    centrifuge.on("connected", () => setConnected(true));
    centrifuge.on("disconnected", () => setConnected(false));

    const sub = centrifuge.newSubscription(first.channel);
    sub.on("publication", (ctx) => announce(ctx.data as LiveEvent));
    sub.subscribe();
    centrifuge.connect();

    client = centrifuge;
    subscription = sub;
  })();

  try {
    await starting;
  } finally {
    starting = null;
  }
}

/**
 * Listen for live events. Returns the unsubscribe function, and opens the
 * connection on the first listener.
 */
export function onLiveEvent(listener: Listener): () => void {
  listeners.add(listener);
  // A token the server refuses — 429 on the hourly allowance, 409 for an account
  // with no handle, 503 while Centrifugo is restarting after a deploy — must not
  // escape as an unhandled rejection. The shell mounts this on every page for
  // every signed-in member, so that would be "Uncaught (in promise)" on every
  // navigation, reported as an application error by anything watching for one.
  // Nothing is lost by catching it: the connection simply did not open, the
  // header already says so through onConnectionChange, and the next mount tries
  // again.
  start().catch(() => {});
  return () => {
    listeners.delete(listener);
    if (listeners.size === 0) stop();
  };
}

/** Watch whether the socket is up, so the UI can say so honestly. */
export function onConnectionChange(listener: (up: boolean) => void): () => void {
  connectionListeners.add(listener);
  listener(connected);
  return () => connectionListeners.delete(listener);
}

export function isConnected(): boolean {
  return connected;
}

/** Close everything. Called when the last listener goes, and on logout. */
export function stop(): void {
  subscription?.unsubscribe();
  subscription?.removeAllListeners();
  client?.disconnect();
  client?.removeAllListeners();
  subscription = null;
  client = null;
  setConnected(false);
}
